import { CartAddEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const WH_CART_UPDATED = 'wishlist-hero-cart-updated';

/**
 * @param {string[]} sectionIds
 * @returns {Promise<Record<string, string>>}
 */
async function fetchCartSections(sectionIds) {
  if (sectionIds.length === 0) return {};

  const entries = await Promise.all(
    sectionIds.map(async (sectionId) => {
      const html = await sectionRenderer.getSectionHTML(sectionId, false);
      return [sectionId, html];
    })
  );

  return Object.fromEntries(entries);
}

/** @type {boolean} */
let syncingCart = false;

/**
 * Wishlist Hero add-to-bag only: stay on page (cart drawer) and remove item from wishlist.
 * Does not affect heart / "View wishlist" popup — that remains WH default.
 */
function patchWishListHeroCartSettings() {
  const apply = (/** @type {Record<string, unknown> | undefined} */ obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    obj.NoRedirectAfterAddToCart = true;
    obj.RemoveFromWishlistAfterAddToCart = true;
    return obj;
  };

  apply(window.WishListHero_setting);
  apply(window.WishListHero_setting_theme_override);

  if (!Object.getOwnPropertyDescriptor(window, 'WishListHero_setting')?.set) {
    let stored = window.WishListHero_setting;
    apply(stored);
    Object.defineProperty(window, 'WishListHero_setting', {
      configurable: true,
      enumerable: true,
      get: () => stored,
      set: (value) => {
        stored = apply(value);
      },
    });
  }
}

patchWishListHeroCartSettings();
document.addEventListener('wishlist-hero-wishlist-sdk-ready', patchWishListHeroCartSettings);

/** After WH adds to cart (single or bulk), refresh theme cart and open cart drawer. */
async function syncThemeCartFromWishlist() {
  if (syncingCart) return;
  syncingCart = true;

  try {
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
          .filter((id) => Boolean(id))
      ),
    ];

    const [cart, sections] = await Promise.all([
      fetch('/cart.js').then((res) => res.json()),
      fetchCartSections(/** @type {string[]} */ (sectionIds)),
    ]);

    document.dispatchEvent(
      new CartAddEvent(cart, 'wishlist-hero-bridge', {
        source: 'wishlist-hero',
        itemCount: cart.item_count,
        sections,
      })
    );
  } catch {
    /* WH already updated Shopify cart */
  } finally {
    window.setTimeout(() => {
      syncingCart = false;
    }, 400);
  }
}

document.addEventListener(WH_CART_UPDATED, () => {
  syncThemeCartFromWishlist().catch(() => {});
});

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const customEvent = /** @type {CustomEvent} */ (event);
  const variant = customEvent.detail?.resource;
  const productId = customEvent.detail?.data?.productId;
  if (!variant?.id) return;

  document.querySelectorAll('.wishlist-hero-custom-button').forEach((button) => {
    if (!(button instanceof HTMLElement)) return;
    if (productId && button.dataset.wlhId !== String(productId)) return;

    button.dataset.wlhVariantid = String(variant.id);

    if (typeof variant.price === 'number') {
      button.dataset.wlhPrice = String(variant.price / 100);
    }

    const imageSrc = variant.featured_media?.preview_image?.src;
    if (imageSrc) {
      button.dataset.wlhImage = imageSrc;
    }

    if (button.closest('#quick-add-dialog')) {
      button.removeAttribute('data-wlh-variantlistener');
      button.removeAttribute('data-wlh-variantListener');
    }

    document.dispatchEvent(new CustomEvent('wishlist-hero-add-to-custom-element', { detail: button }));
  });
});
